import React, { useContext, useState, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import axios from "axios";
import { useAlert } from "react-alert";
import {
  FeatureGatingContext,
  SettingsContext,
  UserContext,
  useEntitlement,
} from "../../../contexts";
import { ActionButton, Visible } from "../../../core/components";
import { ShareHelper } from "../../../helpers";
import {
  hasShareImage,
  prefetchShareImage,
  shareImage,
  shareImageLabel,
} from "../../../helpers/shareImage";
import { canEditDealDate, canMoveTransaction, isDealDateEditable } from "../../../helpers/adminAccess";
import { useItemState, useCreateListing } from "../../hooks";
import { Loading, Select } from "../../../components";
import AdminActionDropDown from "../../../core/components/AdminActionDropDown";
import ChangeSaleLeadModal from "../../../components/ChangeSaleLeadModal.jsx";
import ChangeDealDateModal from "../../../components/ChangeDealDateModal.jsx";
import MoveTransactionModal from "../../../components/MoveTransactionModal.jsx";
import { useSendToWhatsApp, requestSubject } from "../../../waBroadcast";
import { storeCaption } from "../../../waBroadcast/waBroadcastApi";

const actionLabel = (action) => {
  switch (action) {
    case "edit":
      return "Edit";
    case "edit_px":
      return "Edit Part Exchange";
    case "broadcast":
      return "Send via WhatsApp";
    case "copy_image":
      return shareImageLabel();
    case "wa_broadcast":
      return "Share to Broadcast";
    case "send_quote":
      return "Send Quote";
    case "listing_creation":
      return "Listing Creation";
    case "new":
      return "New";
    case "in_progress":
      return "Send Proposal";
    case "negotiation":
      return "Negotiation";
    case "follow_up":
      return "Follow Up";
    case "deposited":
      return "Deposited";
    case "done":
      return "Done";
    case "canceled":
      return "Cancel & Hide";
    case "create_listing":
      return "Create Listing";
    default:
      return "Action";
  }
};

// canShare = Share to Broadcast (Feature 16) is in the store's plan. It was
// always PASSED by the caller below; the parameter was missing, so the reference
// in the body resolved to nothing and every order row on /requests/orders threw
// "canShare is not defined" - taking the whole page with it, since there is no
// error boundary above the list. The sell-side twin (SellItem/AdminActions)
// declared it correctly, which is why only order rows broke. Nothing but a lint
// rule catches this: it compiles.
//
// canChangeDate = finished deal AND the admin holds `edit_deal_date`. Declared
// here for the same reason — the body references it, so it MUST be a parameter.
// canShareImage = the request has a photo. The row sits under Share to Broadcast
// and follows the same plan switch: it is the photo half of the same job, since
// the WhatsApp deeplink can only carry the text.
const getOptions = (
  pxRequestId,
  isQuoteSent,
  isShopify,
  canShare,
  canChangeDate,
  canMove,
  canShareImage,
  canWaBroadcast
) => [
  {
    label: "Actions",
    options: [
      ...(!isShopify && canShare ? [{ id: "broadcast", name: "Send via WhatsApp" }] : []),
      ...(!isShopify && canShare && canShareImage
        ? [{ id: "copy_image", name: shareImageLabel() }]
        : []),
      // WhatsApp Broadcast — master admins post this DAO request to dealer groups.
      ...(canWaBroadcast ? [{ id: "wa_broadcast", name: "Share to Broadcast" }] : []),
      { id: "change_lead", name: "Change Sale Lead" },
      // Only on a finished deal, and only for an admin holding the
      // `edit_deal_date` capability — it restates an accounting month.
      ...(canChangeDate ? [{ id: "change_date", name: "Change Deal Date" }] : []),
      // Re-files the whole deal under another store. Like change_date this is a
      // per-admin grant, not implied by is_admin.
      ...(canMove ? [{ id: "move_transaction", name: "Move Transaction" }] : []),
    ],
  },
  {
    label: "Statuses",
    options: [
      // { id: "new", name: "New" },
      { id: "in_progress", name: "Send Proposal" },
      // { id: "negotiation", name: "Negotiation" },
      // { id: "follow_up", name: "Follow Up" },
      // { id: "deposited", name: "Deposited" },
      // { id: "done", name: "Done" },
      { id: "canceled", name: "Cancel & Hide" },
    ],
  },
];

const AdminActions = ({ item, refreshParent, hide }) => {
  const { checkFeatureQuota } = useContext(FeatureGatingContext);
  const alert = useAlert();
  const history = useHistory();
  const settings = useContext(SettingsContext);
  // Share to Broadcast (Feature 16).
  const canShareBroadcast = useEntitlement("share_to_broadcast");
  const { owner } = useParams();
  const { createListing } = useCreateListing();
  const { startSelection, selectedQuote, sendQuote } = useItemState();
  const { user } = useContext(UserContext) || {};
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [action, setAction] = useState(null);
  const [showSaleLead, setShowSaleLead] = useState(false);
  const [showDealDate, setShowDealDate] = useState(false);
  const [showMove, setShowMove] = useState(false);

  const canChangeDate = canEditDealDate(user) && isDealDateEditable(item);
  // Deliberately NOT gated on the deal being finished, and not gated by the
  // Master Store write guard: a mis-filed deal needs moving at any stage, and
  // the target store is chosen explicitly so there is no ambiguous destination.
  const canMove = canMoveTransaction(user);
  // Hoisted to a boolean so the memo can keep its granular deps: depending on
  // `item` itself would make the field-level deps below redundant.
  const canShareImage = hasShareImage(item);
  const wa = useSendToWhatsApp();
  const canWaBroadcast = !settings.isShopify && wa.canSend(item);

  const options = useMemo(() => {
    if (item.status === "canceled") {
      return [
        {
          label: "Retrieve",
          options: [{ id: "retrieve", name: "Retrieve" }],
        },
      ];
    }
    return getOptions(
      item?.px_request_id,
      !!item.last_quote_id,
      settings.isShopify,
      canShareBroadcast,
      canChangeDate,
      canMove,
      canShareImage,
      canWaBroadcast
    );
  }, [
    canShareImage,
    canWaBroadcast,
    item?.px_request_id,
    item.last_quote_id,
    settings.isShopify,
    item.status,
    canShareBroadcast,
    canChangeDate,
    canMove,
  ]);

  const selectAction = (newAction) => {
    setAction(newAction);
    if (newAction === "send_quote") {
      startSelection();
    }
  };

  const updateStatus = async (action) => {
    try {
      setSubmitting(true);
      setError(false);

      await axios.put(`/api/requests/${item.id}/admin`, {
        status: action,
      });

      if (action === "canceled") {
        window.location.reload();
      } else {
        refreshParent?.();
      }

      setAction(null);
      setSubmitting(false);
    } catch (err) {
      setError(true);
      setAction(null);
      setSubmitting(false);
    }
  };

  const doRetrieve = async () => {
    try {
      setSubmitting(true);
      setError(false);

      await axios.put(`/api/requests/${item.id}/admin`, {
        status: "new",
        hidden: false,
      });

      setAction(null);
      refreshParent?.();
    } catch (err) {
      setError(true);
      setAction(null);
      setSubmitting(false);
    }
  };

  const hideItem = async () => {
    try {
      setSubmitting(true);
      setError(false);

      await axios.put(`/api/requests/${item.id}/admin`, {
        hidden: true,
      });

      setAction(null);
      hide();
    } catch (err) {
      setError(true);
      setAction(null);
      setSubmitting(false);
    }
  };

  const doSendQuote = async () => {
    try {
      setSubmitting(true);
      setError(false);

      await sendQuote();

      setAction(null);
      refreshParent?.();
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.[0] || true);
    } finally {
      setSubmitting(false);
    }
  };

  const doCreateListing = async () => {
    try {
      setSubmitting(true);
      setError(false);

      await createListing(item.last_quote_id);

      setAction(null);
      refreshParent?.();
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.[0] || true);
    } finally {
      setSubmitting(false);
    }
  };

  const updateItem = async (action) => {
    let allowed;

    switch (action) {
      case "edit":
        return history.push(
          `/request/${item.request_type}/${item?.id}?bb=${owner}&${settings.iframeQueryParams}`
        );
      case "edit_px":
        allowed = await checkFeatureQuota(
          FeatureGatingContext.features.PART_EXCHANGE
        );
        if (!allowed) {
          return;
        }

        return history.push(
          `/request/px-order/${item?.px_request_id || ""}?bb=${owner}&${
            settings.iframeQueryParams
          }`,
          { request: item }
        );
      case "broadcast":
        // The store's own template when it has one (Owner Admin → Message templates).
        ShareHelper.whatsapp(
          await storeCaption(
            "request",
            item.id,
            (item.broadcastWhatsappMessage || "")
              .replace(/\\n/g, "\n")
              .replace(/__SQR__/g, "▪️")
          )
        );

        return setAction(null);
      case "copy_image":
        // Not awaited: shareImage must reach clipboard.write() inside the click's
        // user activation, and awaiting here would add nothing — it reports its
        // own outcome through the alerts.
        shareImage(item, {
          onInfo: (message) => alert.info(message),
          onError: (message) => alert.error(message),
        });

        return setAction(null);
      case "wa_broadcast":
        wa.open(requestSubject(item));

        return setAction(null);
      case "listing_creation":
      case "new":
      case "in_progress":
      case "negotiation":
      case "follow_up":
      case "deposited":
      case "done":
      case "canceled":
        return updateStatus(action);
      case "send_quote":
        return doSendQuote();
      case "create_listing":
        return doCreateListing(item.id);
      case "retrieve":
        return doRetrieve();
      case "change_lead":
        return setShowSaleLead(true);
      case "change_date":
        return setShowDealDate(true);
      case "move_transaction":
        return setShowMove(true);
    }
  };

  const goToRequest = () => {
    const url = `/request/${item.px_request_type ||
      item.request_type}/${item?.px_request_id || item?.id}?${
      settings.iframeQueryParams
    }`;
    window.open(url, '_blank');
  };

  return (
    <>
      <AdminActionDropDown
        options={options}
        setAction={setAction}
        updateItem={updateItem}
        onClick={goToRequest}
        onOpen={() => prefetchShareImage(item)}
      />
      {wa.sheet}
      {showSaleLead && (
        <ChangeSaleLeadModal
          onClose={() => setShowSaleLead(false)}
          onConfirm={async (userId) => {
            await axios.put(`/api/requests/${item.id}/sale-lead`, { user_id: userId });
            setShowSaleLead(false);
            refreshParent?.();
          }}
        />
      )}
      {showDealDate && (
        <ChangeDealDateModal
          currentDate={item.created_at}
          onClose={() => {
            setShowDealDate(false);
            // Refresh on close, not only on success: a change that landed in RC
            // but failed in Xero keeps the modal open on a warning, so the list
            // behind it is already stale by the time the rep dismisses it.
            refreshParent?.();
          }}
          onConfirm={async (date) => {
            const { data } = await axios.put(
              `/api/requests/${item.id}/deal-date`,
              { date },
              { metadata: { silent: true } }
            );
            return data;
          }}
        />
      )}
      {showMove && (
        <MoveTransactionModal
          requestId={item.id}
          onClose={() => {
            setShowMove(false);
            // Refresh on close, not only on success: a move whose Xero void
            // failed keeps the modal open on a warning, so the list behind it is
            // already stale by the time the rep dismisses it.
            refreshParent?.();
          }}
          onMoved={(storeName) =>
            alert.show(`Moved to ${storeName}. Switch store to view it.`, {
              type: "success",
            })
          }
        />
      )}
    </>
  );
};

export default AdminActions;
